/**
 * 记账同步工作台（财务记账岗）
 *
 * ⚠️ 本页最重要的一句话写在页面最上方，且不允许被折叠：
 *     「请在 T+ 端选择会计科目生成凭证，本平台仅同步业务数据」
 *
 * 平台的角色是「把发票业务数据放进 T+ 待办业务池」，仅此而已。
 * 因此：
 *   - 页面上不提供任何「生成凭证」「一键入账」类按钮，也不显示任何暗示系统会生成凭证的文案；
 *   - 凭证号、入账时间、入账状态三个字段全部只读展示，来源注明「T+ 回写」；
 *   - 推送失败时只提供「重试推送」，不提供「手工填凭证号」的旁路。
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CloudSyncOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  INVOICE_TYPE_LABELS,
  InvoiceStatus,
  PermissionCode,
  TPLUS_SYNC_STATUS_LABELS,
  TplusSyncStatus,
  shouldBlockTplusPush,
  type InvoiceListItem,
  type TplusSync,
} from '@ilc/shared';
import { invoiceApi } from '@/api/endpoints';
import { mockInvoiceList } from '@/mocks';
import { usePagedList } from '@/hooks/usePagedList';
import { useAuthStore } from '@/stores/auth';
import { DataTable, BatchBar } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { RiskTag } from '@/components/RiskTag';
import { InvoiceStatusTag } from '@/components/InvoiceStatusTag';
import { Money } from '@/components/Money';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';

interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  /** 只看某一推送状态 */
  syncStatus?: TplusSyncStatus | 'PENDING_PUSH';
}

export default function BookkeeperWorkbench(): React.ReactElement {
  const navigate = useNavigate();
  const can = useAuthStore((s) => s.can);
  const canPush = can(PermissionCode.TPLUS_PUSH);

  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [busy, setBusy] = useState(false);
  const [payloadTarget, setPayloadTarget] = useState<InvoiceListItem | null>(null);

  const list = usePagedList<InvoiceListItem, Query>({
    loader: (q) => invoiceApi.list(q),
    fallback: (q) => mockInvoiceList(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const rows = list.list;
  const stats = {
    pendingPush: rows.filter((r) => r.invoiceStatus === InvoiceStatus.APPROVED).length,
    pushed: rows.filter((r) => r.tplus?.status === TplusSyncStatus.PUSHED).length,
    booked: rows.filter((r) => r.tplus?.status === TplusSyncStatus.BOOKED).length,
    failed: rows.filter((r) => r.tplus?.status === TplusSyncStatus.FAILED).length,
  };

  const visibleRows = rows.filter((row) => {
    const filter = list.query.syncStatus;
    if (!filter) return true;
    if (filter === 'PENDING_PUSH') return row.invoiceStatus === InvoiceStatus.APPROVED;
    return row.tplus?.status === filter;
  });

  const pushOne = async (record: InvoiceListItem): Promise<void> => {
    setBusy(true);
    const result = await performAction({
      action: () => invoiceApi.pushTplus(record.id, {}),
      successMessage: `${record.bizNo} 已推送至 T+ 待办业务池，请到 T+ 端选择会计科目生成凭证`,
      offline: () =>
        list.setList((prev) =>
          prev.map((item) =>
            item.id === record.id
              ? { ...item, invoiceStatus: InvoiceStatus.PUSHED }
              : item,
          ),
        ),
      offlineMessage: '后端推送接口尚未就绪，本次推送仅在本地演示，T+ 侧不会收到数据',
    });
    setBusy(false);
    if (result.ok) list.reload();
  };

  const pushBatch = async (): Promise<void> => {
    const targets = visibleRows.filter(
      (r) => selectedKeys.includes(r.id) && r.invoiceStatus === InvoiceStatus.APPROVED,
    );
    if (targets.length === 0) {
      feedback.warning('所选发票中没有「初审通过」状态的记录，无法推送');
      return;
    }
    const blocked = targets.filter((t) => shouldBlockTplusPush(t.riskItems ?? []).blocked);
    if (blocked.length > 0) {
      feedback.error(
        `所选发票中有 ${blocked.length} 张为高风险（默认阻断推送），请先在详情页完成特例放行`,
      );
      return;
    }
    setBusy(true);
    let ok = 0;
    for (const target of targets) {
      const result = await performAction({
        action: () => invoiceApi.pushTplus(target.id, {}),
        successMessage: `${target.bizNo} 已推送`,
        offline: () => undefined,
      });
      if (result.ok) ok += 1;
    }
    setBusy(false);
    setSelectedKeys([]);
    if (ok > 0) {
      feedback.info(`共推送 ${ok} 张，请到 T+ 端选择会计科目生成凭证`);
      list.reload();
    }
  };

  type RowWithSync = InvoiceListItem & { tplus?: TplusSync | null };

  return (
    <div className="ilc-page">
      <PageHeader
        title="记账同步"
        description="查看发票数据推送 T+ 的结果与凭证回写情况。本页面不产生任何会计凭证。"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={list.reload}>
              刷新
            </Button>
            <Button icon={<SyncOutlined />} onClick={() => setPayloadTarget(null)} disabled>
              同步状态自动刷新
            </Button>
          </Space>
        }
      />

      {/* —— 最重要的一句话：固定在最上方，不折叠、不隐藏 —— */}
      <Alert
        type="warning"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 12 }}
        message="请在 T+ 端选择会计科目生成凭证，本平台仅同步业务数据"
        description={
          <Space direction="vertical" size={4}>
            <span>
              本平台<strong>绝不自动生成凭证</strong>：推送动作只是把发票的业务数据（票种、号码、金额、税额、销方、
              合同号）写入 T+ 的「待办业务池」，凭证号、入账时间、入账状态三个字段由 T+ 端生成后回写，本平台只读展示。
            </span>
            <span style={{ color: 'rgba(0,0,0,0.45)' }}>
              如果 T+ 待办业务池中没有出现对应的单据，请先在本页确认推送状态为「已推送至 T+」，
              再联系 T+ 侧管理员核对账套与接口配置。
            </span>
          </Space>
        }
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="待推送（初审通过）" value={stats.pendingPush} suffix="张" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="已推送，待 T+ 端记账"
              value={stats.pushed}
              suffix="张"
              valueStyle={{ color: '#0958d9' }}
              prefix={<CloudSyncOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="T+ 已生成凭证并回写" value={stats.booked} suffix="张" valueStyle={{ color: '#389e0d' }} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="推送失败"
              value={stats.failed}
              suffix="张"
              valueStyle={{ color: stats.failed > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <DataTable<RowWithSync>
        storageKey="bookkeeper-invoices"
        exportFileName="记账同步"
        columns={[
          {
            title: '内部单号',
            dataIndex: 'bizNo',
            width: 168,
            fixed: 'left',
            render: (value: string, record) => (
              <a className="ilc-mono" style={{ fontSize: 12 }} onClick={() => navigate(`/business/invoices/${record.id}`)}>
                {value}
              </a>
            ),
          },
          {
            title: '发票号码',
            dataIndex: 'invoiceNo',
            width: 180,
            render: (value: string, record) => (
              <Space direction="vertical" size={0}>
                <span className="ilc-mono" style={{ fontSize: 12 }}>
                  {value}
                </span>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {INVOICE_TYPE_LABELS[record.invoiceType] ?? record.invoiceType}
                </Typography.Text>
              </Space>
            ),
          },
          { title: '销方名称', dataIndex: 'sellerName', width: 200, ellipsis: true, render: (v: string | null) => v ?? '-' },
          {
            title: '价税合计',
            dataIndex: 'totalCents',
            width: 150,
            align: 'right',
            render: (value: number | null) => <Money cents={value} withChinese strong />,
          },
          {
            title: '风险',
            dataIndex: 'riskLevel',
            width: 120,
            render: (value: InvoiceListItem['riskLevel'], record) => (
              <Tooltip title={shouldBlockTplusPush(record.riskItems ?? []).blocked ? '高风险，默认阻断推送 T+，需特例放行' : undefined}>
                <span>
                  <RiskTag level={value} />
                </span>
              </Tooltip>
            ),
          },
          {
            title: '发票状态',
            dataIndex: 'invoiceStatus',
            width: 120,
            render: (value: InvoiceListItem['invoiceStatus']) => <InvoiceStatusTag status={value} />,
          },
          {
            title: '推送状态',
            key: 'syncStatus',
            width: 140,
            render: (_v, record) => {
              const sync = record.tplus;
              if (!sync) {
                return record.invoiceStatus === InvoiceStatus.APPROVED ? (
                  <Tag color="blue">待推送</Tag>
                ) : (
                  <Tag>尚未推送</Tag>
                );
              }
              const color =
                sync.status === TplusSyncStatus.BOOKED
                  ? 'green'
                  : sync.status === TplusSyncStatus.FAILED
                    ? 'red'
                    : sync.status === TplusSyncStatus.PUSHED
                      ? 'blue'
                      : 'default';
              return <Tag color={color}>{TPLUS_SYNC_STATUS_LABELS[sync.status] ?? sync.status}</Tag>;
            },
          },
          {
            title: 'T+ 单据号',
            key: 'tplusBillNo',
            width: 170,
            render: (_v, record) =>
              record.tplus?.tplusBillNo ? (
                <span className="ilc-mono" style={{ fontSize: 12 }}>
                  {record.tplus.tplusBillNo}
                </span>
              ) : (
                '-'
              ),
          },
          {
            title: '凭证号（T+ 回写）',
            key: 'voucherNo',
            width: 180,
            render: (_v, record) => {
              const voucherNo = record.tplus?.voucherNo;
              if (!voucherNo) {
                return (
                  <Tooltip title="凭证由财务人员在 T+ 端选择会计科目生成，本平台不生成凭证，仅接收凭证号回写">
                    <Tag color="orange">待 T+ 端生成</Tag>
                  </Tooltip>
                );
              }
              return (
                <Space direction="vertical" size={0}>
                  <span className="ilc-mono" style={{ fontSize: 12 }}>
                    {voucherNo}
                  </span>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {record.tplus?.bookingStatus ?? ''}
                  </Typography.Text>
                </Space>
              );
            },
          },
          {
            title: '操作',
            key: 'action',
            width: 200,
            fixed: 'right',
            render: (_v, record) => (
              <Space size={4}>
                <Button size="small" type="link" onClick={() => navigate(`/business/invoices/${record.id}`)}>
                  详情
                </Button>
                {record.invoiceStatus === InvoiceStatus.APPROVED ? (
                  <Button
                    size="small"
                    type="link"
                    disabled={!canPush || shouldBlockTplusPush(record.riskItems ?? []).blocked}
                    onClick={() => void pushOne(record)}
                  >
                    推送 T+
                  </Button>
                ) : null}
                {record.tplus?.status === TplusSyncStatus.FAILED ? (
                  <Button size="small" type="link" danger onClick={() => void pushOne(record)}>
                    重试推送
                  </Button>
                ) : null}
                {record.tplus ? (
                  <Button size="small" type="link" onClick={() => setPayloadTarget(record)}>
                    报文
                  </Button>
                ) : null}
              </Space>
            ),
          },
        ]}
        dataSource={visibleRows}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        emptyText="当前没有需要同步的发票：初审通过的发票会自动进入待推送列表"
        rowSelection={{ selectedRowKeys: selectedKeys, onChange: (keys) => setSelectedKeys(keys) }}
        footer={() =>
          selectedKeys.length > 0 ? (
            <BatchBar count={selectedKeys.length} onClear={() => setSelectedKeys([])}>
              <Button
                size="small"
                type="primary"
                icon={<UploadOutlined />}
                loading={busy}
                disabled={!canPush}
                onClick={() => void pushBatch()}
              >
                批量推送至 T+
              </Button>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                高风险发票会被自动过滤，需先在详情页完成特例放行
              </Typography.Text>
            </BatchBar>
          ) : null
        }
        toolbar={
          <Space wrap>
            <Input.Search
              placeholder="内部单号 / 发票号码 / 销方名称"
              allowClear
              style={{ width: 260 }}
              onSearch={(value) => list.setQuery({ keyword: value })}
            />
            <Select
              placeholder="推送状态"
              allowClear
              style={{ width: 170 }}
              value={list.query.syncStatus}
              onChange={(value) => list.setQuery({ syncStatus: value })}
              options={[
                { value: 'PENDING_PUSH', label: '待推送（初审通过）' },
                ...Object.entries(TPLUS_SYNC_STATUS_LABELS).map(([value, label]) => ({ value, label })),
              ]}
            />
          </Space>
        }
      />

      {!canPush ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="当前账号为只读查看"
          description="您没有 tplus:push 权限，可以查看推送与记账结果，但无法发起推送。如需操作请联系租户管理员。"
        />
      ) : null}

      <Card size="small" title="T+ 同步说明" style={{ marginTop: 16 }}>
        <ul style={{ margin: 0, paddingInlineStart: 18, lineHeight: 2, fontSize: 13 }}>
          <li>
            <strong>推送什么</strong>：票种、发票代码/号码、开票日期、销方与购方信息、金额与税额、关联合同号、
            校验报告编号。推送报文原样归档，可点行内「报文」查看。
          </li>
          <li>
            <strong>不推送什么</strong>：不推送会计科目、不推送借贷方向、不推送凭证号——这些属于 T+ 端的会计判断，
            平台不越界。
          </li>
          <li>
            <strong>回写什么</strong>：T+ 生成凭证后回写凭证号、入账时间、入账状态。回写接口带签名校验，
            平台侧对这三个字段为只读，接口层面也拒绝本系统写入。
          </li>
          <li>
            <strong>失败怎么办</strong>：推送失败会在列表中标红并可重试；连续失败请技术实施组在
            「接口配置 → 接口调用日志」中查看原始报文。
          </li>
        </ul>
      </Card>

      <Modal
        open={payloadTarget !== null}
        title={`T+ 推送报文 —— ${payloadTarget?.bizNo ?? ''}`}
        onCancel={() => setPayloadTarget(null)}
        footer={
          <Button onClick={() => setPayloadTarget(null)}>关闭</Button>
        }
        width={720}
      >
        {payloadTarget?.tplus ? (
          <>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="推送状态">
                {TPLUS_SYNC_STATUS_LABELS[payloadTarget.tplus.status] ?? payloadTarget.tplus.status}
              </Descriptions.Item>
              <Descriptions.Item label="账套">{payloadTarget.tplus.accountSet ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="T+ 单据号">
                <span className="ilc-mono">{payloadTarget.tplus.tplusBillNo ?? '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="推送时间">
                {payloadTarget.tplus.pushedAt?.replace('T', ' ').slice(0, 19) ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="凭证号（T+ 回写）">
                {payloadTarget.tplus.voucherNo ?? '尚未生成'}
              </Descriptions.Item>
              <Descriptions.Item label="入账状态">
                {payloadTarget.tplus.bookingStatus ?? '待 T+ 端记账'}
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message="以下报文仅用于排查接口问题，其中的凭证字段为空属正常——凭证由 T+ 端生成后回写"
            />
            <pre
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                padding: 12,
                maxHeight: 320,
                overflow: 'auto',
                fontSize: 12,
                fontFamily: 'var(--ilc-mono)',
              }}
            >
              {JSON.stringify(
                {
                  pushPayload: payloadTarget.tplus.pushPayload,
                  pushResponse: payloadTarget.tplus.pushResponse,
                  callbackPayload: payloadTarget.tplus.callbackPayload,
                },
                null,
                2,
              )}
            </pre>
          </>
        ) : (
          <Typography.Text type="secondary">该发票尚无推送记录</Typography.Text>
        )}
      </Modal>

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
        推送按钮对高风险发票自动禁用（需先完成特例放行），且要求发票处于「初审通过」状态；
        可放行的风险等级与审核金额上限见「个人信息 → 权限范围」。
      </Typography.Paragraph>
    </div>
  );
}
